import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Typography, Flex, Button } from "antd";
import {
  CpuChipIcon,
  BookmarkIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useBookmarkToggle } from "../../hooks/useBookmarkToggle";
import { CatalogItem } from "../../types";
import { getSpeciesDisplay } from "../../utils/species";
import SpecimenDownloadModal from "./SpecimenDownloadModal";

const { Text } = Typography;

interface SpecimenHeaderProps {
  id: string | undefined;
  displayTitle: string | undefined;
  isMeshRoute: boolean;
  catalogItem: CatalogItem | undefined;
  onDownload: () => void;
}

const SpecimenHeader: React.FC<SpecimenHeaderProps> = ({
  id,
  displayTitle = "",
  isMeshRoute,
  catalogItem,
  onDownload,
}) => {
  const { isBookmarking, handleToggleBookmark } = useBookmarkToggle({
    catalogItem: catalogItem!,
  });

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const { user } = useAuth0();

  const species =
    catalogItem?.source === "detection"
      ? getSpeciesDisplay(catalogItem, user?.email)
      : undefined;

  return (
    <>
      <Flex justify="space-between" align="center">
        <div>
          <Text className="text-[40px]! leading-10!">{displayTitle}</Text>
          <Flex
            align="center"
            gap="10px"
            className={`pt-2! ${isMeshRoute ? "invisible" : ""}`}
          >
            <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-[#EBF5FF]">
              <CpuChipIcon className="w-5 h-5 text-[#6791C0]" />
            </span>
            <Text className="text-[#6B7280]">
              {species ? (
                <>
                  <span
                    className={`font-semibold text-[#4A87C7] ${species.isItalic ? "italic" : ""}`}
                  >
                    {species.label}
                  </span>
                  <span className="ml-1.5">
                    {species.contributor
                      ? `determined by ${species.contributor}`
                      : "detected by Vectech Scout"}
                  </span>
                  {species.overridden && (
                    <span className="text-[#9CA3AF]">
                      {" \u00b7 Scout said "}
                      <span
                        className={
                          species.overridden.isItalic ? "italic" : undefined
                        }
                      >
                        {species.overridden.label}
                      </span>
                    </span>
                  )}
                </>
              ) : (
                "Mosquito detected by Vectech Scout"
              )}
            </Text>
          </Flex>
        </div>

        <Flex gap="16px">
          <Button
            type="primary"
            loading={isBookmarking}
            onClick={handleToggleBookmark}
            disabled={!catalogItem}
            className={catalogItem?.is_bookmarked ? "bg-[#005F8F]!" : ""}
          >
            <Flex align="center" justify="center" gap="8px">
              {catalogItem?.is_bookmarked ? (
                <BookmarkIcon
                  className="w-5 h-5  border-[#ffffff] "
                  fill="#005F8F"
                />
              ) : (
                <BookmarkIcon className="w-5 h-5 text-[#FFFFFF]" />
              )}
              {catalogItem?.is_bookmarked ? "Bookmarked" : "Bookmark"}
            </Flex>
          </Button>
          <Button type="primary" onClick={() => setIsDownloadModalOpen(true)}>
            <Flex align="center" justify="center" gap="8px">
              <ArrowDownTrayIcon className="w-5 h-5" />
              Download
            </Flex>
          </Button>
        </Flex>
      </Flex>

      <SpecimenDownloadModal
        open={isDownloadModalOpen}
        onCancel={() => setIsDownloadModalOpen(false)}
        onDownload={() => {
          onDownload();
          setIsDownloadModalOpen(false);
        }}
        isMeshRoute={isMeshRoute}
      />
    </>
  );
};

export default SpecimenHeader;
